import { Stack } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { logout } from "@src/app/store/session/actions";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Input,
  Text,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/system/ui-theme";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { isAxiosError } from "axios";
import { useState } from "react";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";

import { EmailAlreadyInUseError } from "../../api/errors/emailAlreadyInUse.error";
import { InvalidEmailError } from "../../api/errors/invalidEmail.error";
import { useUpdateOnboardingEmail } from "../../api/useUpdateOnboardingEmail";
import { OnboardingTitle } from "../../SharedTitle";
import { ONBOARDING_STAGES } from "../../util/constants";
import {
  type OnboardingStepProps,
  OnboardingStepWrapper,
  type OnboardingStepWrapperProps,
} from "../OnboardingStepWrapper";

function EmailStepContent(props: OnboardingStepProps) {
  const { onNext, worker } = props;
  const dispatch = useDispatch();
  const { showInfoToast } = useToast();
  const [email, setEmail] = useState<string | undefined>(worker?.email);
  const [emailError, setEmailError] = useState<
    | {
        message: string;
        errorCode: "InvalidEmailError" | "EmailAlreadyInUseError" | "UnexpectedError";
      }
    | undefined
  >(undefined);

  const emailAlreadyInUseModalState = useModalState();

  const { mutateAsync: updateOnboardingEmail, status: updateOnboardingEmailStatus } =
    useUpdateOnboardingEmail();

  return (
    <>
      <PageContent>
        <Stack gap={4}>
          <OnboardingTitle text="Welcome to Clipboard!" />
          <Text semibold variant="body1">
            Let&apos;s start with an email to get your account setup
          </Text>
        </Stack>
        <Stack
          direction="column"
          justifyContent="space-between"
          sx={{
            height: "100%",
            marginTop: 6,
          }}
        >
          <Stack spacing={3}>
            <Text
              semibold
              variant="body2"
              sx={{ color: (theme) => theme.palette.grey[700], paddingLeft: 1 }}
            >
              Email address
            </Text>

            <Input
              fullWidth
              disabled={updateOnboardingEmailStatus === "loading"}
              name="email"
              type="email"
              value={email ?? worker?.email}
              error={Boolean(emailError)}
              inputProps={{
                "aria-label": "Email Address",
              }}
              placeholder="janesmith@gmail.com"
              size="large"
              onChange={(event) => {
                setEmailError(undefined);
                setEmail(event.target.value);
              }}
            />
            {emailError && (
              <Text variant="caption" color="error" sx={{ paddingLeft: 1 }}>
                {emailError.message}
              </Text>
            )}
          </Stack>
        </Stack>
        <BottomSheet
          modalState={emailAlreadyInUseModalState}
          paperSx={(theme) => ({
            ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
          })}
          header={<BottomSheetHeader onClose={emailAlreadyInUseModalState.closeModal} />}
          footer={
            <DialogFooter orientation="vertical">
              <Button
                fullWidth
                variant="brand"
                size="large"
                onClick={() => {
                  emailAlreadyInUseModalState.closeModal();
                  dispatch(logout());
                }}
              >
                Log in
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => {
                  emailAlreadyInUseModalState.closeModal();
                  setEmail("");
                  setEmailError(undefined);
                }}
              >
                Use different email
              </Button>
            </DialogFooter>
          }
        >
          <BottomSheetIllustrationContent
            illustrationType="warning"
            title="Email already in use"
            description={
              <Text>
                The email you entered <strong>{email}</strong>, is already linked to a Clipboard
                account. Login to continue or use different email
              </Text>
            }
          />
        </BottomSheet>
      </PageContent>
      <PageFooterWrapper>
        <Stack direction="column" spacing={2}>
          <LoadingButton
            fullWidth
            variant="brand"
            size="large"
            isLoading={updateOnboardingEmailStatus === "loading"}
            disabled={false}
            onClick={async () => {
              setEmailError(undefined);

              const normalizedWorkerEmail = worker?.email?.trim().toLowerCase();
              const normalizedInputEmail = email?.trim().toLowerCase();

              if (!normalizedInputEmail) {
                setEmailError({ message: "Email is required", errorCode: "InvalidEmailError" });
                return;
              }

              if (normalizedWorkerEmail === normalizedInputEmail) {
                void onNext();
                return;
              }

              if (updateOnboardingEmailStatus === "loading") {
                showInfoToast("Please wait while we save your email");
                return;
              }

              try {
                await updateOnboardingEmail({ newEmail: normalizedInputEmail });
                void onNext();
              } catch (error: unknown) {
                const metadata = {
                  email: normalizedInputEmail,
                  workerId: worker?.userId,
                  errorCode: "UnexpectedError",
                };

                if (error instanceof InvalidEmailError) {
                  const errorCode = "InvalidEmailError";
                  setEmailError({ message: error.message, errorCode });
                  logError(APP_V2_APP_EVENTS.ONBOARDING_UPDATE_EMAIL_ERROR, {
                    error,
                    metadata: { ...metadata, errorCode },
                  });
                  return;
                }

                if (error instanceof EmailAlreadyInUseError) {
                  const errorCode = "EmailAlreadyInUseError";
                  emailAlreadyInUseModalState.openModal();
                  setEmailError({ message: "Email already taken", errorCode });
                  logError(APP_V2_APP_EVENTS.ONBOARDING_UPDATE_EMAIL_ERROR, {
                    error,
                    metadata: { ...metadata, errorCode },
                  });
                  return;
                }

                if (
                  error instanceof Error &&
                  error.message === "No authentication token available"
                ) {
                  setEmailError({
                    message: "Your session has expired. Please log in again.",
                    errorCode: "UnexpectedError",
                  });
                  logError(APP_V2_APP_EVENTS.ONBOARDING_SESSION_EXPIRED_ERROR, {
                    error,
                    metadata: { workerId: worker?.userId, step: ONBOARDING_STAGES.EMAIL },
                  });
                  return;
                }

                if (isAxiosError(error) && error.response?.status === 422) {
                  setEmailError({
                    message: "There was a conflict with your account. Please contact support.",
                    errorCode: "UnexpectedError",
                  });
                  logError(APP_V2_APP_EVENTS.ONBOARDING_ACCOUNT_CONFLICT_ERROR, {
                    error,
                    metadata: { workerId: worker?.userId, step: ONBOARDING_STAGES.EMAIL },
                  });
                  return;
                }

                setEmailError({
                  message: "Unexpected error occurred while saving email. Please try again.",
                  errorCode: "UnexpectedError",
                });

                logError(APP_V2_APP_EVENTS.ONBOARDING_UPDATE_EMAIL_ERROR, {
                  error,
                  metadata,
                });
              }
            }}
          >
            Continue
          </LoadingButton>
        </Stack>
      </PageFooterWrapper>
    </>
  );
}

export function EmailStep(
  props: Pick<OnboardingStepWrapperProps, "worker" | "progress" | "onBack" | "onNext" | "stage">
) {
  return (
    <OnboardingStepWrapper
      {...props}
      render={(stepProps: OnboardingStepProps) => <EmailStepContent {...stepProps} />}
    />
  );
}
